/// <reference path="../types.d.ts" />

import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { CapturedTime } from './action-types';
import { cmxDateTime, HttpMethods } from '@codametrix/ui-common';
import { api } from '../core/net';
import { showFeedback } from './feedback.actions';

const toggleIsOpenCapturedTime = createAction<boolean>(
  CapturedTime.TOGGLE_IS_OPEN_CAPTURED_TIME
);

const listCapturedTimeForWeek = createAsyncThunk<
  CMxAPI.PageableList<CMxAPI.NormalTimeDay>,
  {
    startDate: Date;
    endDate: Date;
    user: CMxAPI.User;
    context: CMx.Context;
  },
  { rejectValue: CMxCommonApp.SubmitError }
>(CapturedTime.LIST_CAPTURED_TIME_FOR_WEEK, async (payload, _thunkAPI) => {
  const { startDate, endDate, user, context } = payload;

  const searchParams: URLSearchParams = new URLSearchParams({
    userUid: user.userId,
    tenantId: context.activeContext?.tenantId!,
    startDate: cmxDateTime.format(startDate, cmxDateTime.FORMATS.DATE),
    endDate: cmxDateTime.format(endDate, cmxDateTime.FORMATS.DATE)
  });

  const capturedTimeWeek = await api<CMxAPI.PageableList<CMxAPI.NormalTimeDay>>(
    {
      endpoint: `/api/coder-activity/work-log/normal/user/tenant/v1?${searchParams.toString()}`,
      init: {
        method: HttpMethods.GET
      }
    }
  );
  return capturedTimeWeek;
});

const listProtectedTimeForWeek = createAsyncThunk<
  CMxAPI.PageableList<CMxAPI.ProtectedTime>,
  {
    startDate: Date;
    endDate: Date;
    user: CMxAPI.User;
    context: CMx.Context;
  },
  { rejectValue: CMxCommonApp.SubmitError }
>(CapturedTime.LIST_PROTECTED_TIME_FOR_WEEK, async (payload, _thunkAPI) => {
  const { startDate, endDate, user, context } = payload;

  const searchParams: URLSearchParams = new URLSearchParams({
    userUid: user.userId,
    tenantId: context.activeContext?.tenantId!,
    startDate: cmxDateTime.format(startDate, cmxDateTime.FORMATS.DATE),
    endDate: cmxDateTime.format(endDate, cmxDateTime.FORMATS.DATE)
  });

  const protectedTimeWeek = await api<CMxAPI.PageableList<CMxAPI.ProtectedTime>>(
    {
      endpoint: `/api/coder-activity/work-log/protected/user/tenant/v1?${searchParams.toString()}`,
      init: {
        method: HttpMethods.GET
      }
    }
  );
  return protectedTimeWeek;
});

const deleteProtectedTimeEntry = createAsyncThunk<
  void,
  { workLogId: string },
  { rejectValue: CMxCommonApp.SubmitError }
>(CapturedTime.DELETE_PROTECTED_TIME_ENTRY, async (payload, { dispatch }) => {
  const { workLogId } = payload;

  const searchParams = new URLSearchParams({
    workLogId: workLogId
  });

  try {
    await api<void>({
      endpoint: `/api/coder-activity/work-log/protected/edit/v1?${searchParams.toString()}`,
      init: {
        method: HttpMethods.DELETE
      }
    });
    return;
  } catch (error) {
    dispatch(
      showFeedback({
        id: Date.now(),
        message: `Time entry was not deleted correctly, error message ${error}`,
        dismissable: true
      }) as any
    );
    return;
  }
});

const updateProtectedTimeEntry = createAsyncThunk<
  CMxAPI.ProtectedTime,
  { workLogId: string; workLog: CMxAPI.ProtectedTime },
  { rejectValue: CMxCommonApp.SubmitError }
>(CapturedTime.UPDATE_PROTECTED_TIME_ENTRY, async (payload, _thunkAPI) => {
  const { workLogId, workLog } = payload;
  const updatedProtectedTimeEntry = await api<CMxAPI.ProtectedTime>({
    endpoint: `/api/coder-activity/work-log/protected/edit/${workLogId}/v1`,
    init: {
      method: HttpMethods.PUT
    },
    body: workLog
  });
  return updatedProtectedTimeEntry;
});

const addProtectedTimeEntry = createAsyncThunk<
  CMxAPI.ProtectedTime,
  { workLogProtectedRequestDto: CMxAPI.ProtectedTime },
  { rejectValue: CMxCommonApp.SubmitError }
>(CapturedTime.ADD_PROTECTED_TIME_ENTRY, async (payload, _thunkAPI) => {
  const { workLogProtectedRequestDto } = payload;
  const newProtectedTimeEntry = await api<CMxAPI.ProtectedTime>({
    endpoint: `/api/coder-activity/work-log/protected/edit/v1`,
    init: {
      method: HttpMethods.POST
    },
    body: workLogProtectedRequestDto
  });
  return newProtectedTimeEntry;
});

const getMaxAllowedBackdate = createAsyncThunk<
  CMxAPI.ConfigurationValue[],
  { key: string; tenantId: string },
  { rejectValue: CMxCommonApp.SubmitError }
>(CapturedTime.GET_MAX_ALLOWED_BACKDATE, async (payload, _thunkAPI) => {
  const { key, tenantId } = payload;

  const searchParams = new URLSearchParams({
    key: key,
    tenantId: tenantId
  });

  const maxAllowedBackdateDays = await api<CMxAPI.ConfigurationValue[]>({
    endpoint: `/configuration/value/v1?${searchParams.toString()}`,
    init: {
      method: HttpMethods.GET
    }
  });
  return maxAllowedBackdateDays;
});

export {
  toggleIsOpenCapturedTime,
  listCapturedTimeForWeek,
  listProtectedTimeForWeek,
  deleteProtectedTimeEntry,
  updateProtectedTimeEntry,
  addProtectedTimeEntry,
  getMaxAllowedBackdate
};
